// 실시간 브로커(STOMP) 연결 유지·재접속 규칙입니다.
// 2초 하트비트와 5초 고정 재접속은 가려진 탭에서 끊김·재접속을 반복해 브로커 접속이 하루 수만 건으로
// 늘었습니다. 하트비트를 늘리고, 실패할수록 기다리는 시간을 늘리며, 가려진 탭은 다시 보일 때 접속합니다.

export const STOMP_HEARTBEAT_MS = 10000;
export const RECONNECT_BASE_MS = 5000;
export const RECONNECT_MAX_MS = 60000;
export const RECONNECT_JITTER = 0.2;

/** 연결 객체에 하트비트(보내기·받기)를 설정합니다. */
export function applyStompHeartbeat(client) {
  client.heartbeat.outgoing = STOMP_HEARTBEAT_MS;
  client.heartbeat.incoming = STOMP_HEARTBEAT_MS;
}

/**
 * attempt번째(0부터) 재접속 대기 시간: 5 → 10 → 20 → 40 → 60초(최대).
 * 여러 브라우저가 같은 순간에 몰리지 않도록 ±20% 무작위 편차를 줍니다.
 */
export function reconnectDelay(attempt, random = Math.random) {
  const step = Math.max(0, attempt);
  const base = Math.min(RECONNECT_BASE_MS * 2 ** step, RECONNECT_MAX_MS);
  const jitter = (random() * 2 - 1) * RECONNECT_JITTER;
  return Math.round(base * (1 + jitter));
}

export function isPageHidden(doc = document) {
  return doc?.visibilityState === 'hidden';
}

/**
 * 재접속 예약기를 만듭니다. 화면(레이아웃)은 연결 실패 때 schedule, 성공 때 connected,
 * 화면 종료·로그아웃 때 cancel만 부르면 됩니다.
 *
 * - 예약은 한 번에 하나만 둡니다(연속 오류에도 타이머 하나).
 * - 탭이 가려져 있으면 예약하지 않고, 다시 보이는 순간 바로 접속합니다.
 * - canReconnect가 false(화면 종료·토큰 없음)이면 아무것도 하지 않습니다.
 */
export function createStompReconnector({
  connect,
  canReconnect,
  doc = document,
  random = Math.random,
}) {
  let attempt = 0;
  let timer = null;
  let waitingForVisible = false;

  const reconnectNow = () => {
    if (!canReconnect()) return;
    if (isPageHidden(doc)) {
      waitingForVisible = true;
      return;
    }
    connect();
  };

  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    waitingForVisible = false;
  };

  const handleVisibilityChange = () => {
    if (!waitingForVisible || isPageHidden(doc)) return;
    waitingForVisible = false;
    reconnectNow();
  };

  return {
    schedule() {
      if (timer || waitingForVisible || !canReconnect()) return;
      if (isPageHidden(doc)) {
        waitingForVisible = true;
        return;
      }
      const delay = reconnectDelay(attempt, random);
      attempt += 1;
      timer = setTimeout(() => {
        timer = null;
        reconnectNow();
      }, delay);
    },
    connected() {
      attempt = 0;
      waitingForVisible = false;
    },
    cancel,
    start() {
      doc?.addEventListener?.('visibilitychange', handleVisibilityChange);
    },
    stop() {
      cancel();
      doc?.removeEventListener?.('visibilitychange', handleVisibilityChange);
    },
  };
}
